import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import _ from 'lodash';
import { Partner } from 'types';
import { Delete, Edit, Visibility } from '@mui/icons-material';
import { Button } from 'components/ui/button';
import CreateUserSettings from './CreatePartnerSettings';
import ShowPartnerSetting from './ShowPartnerSettings/ShowPartnerSetting';
import styles from '../GeneralSettings.module.scss';
import EditUserSettings from './EditPartnerSettings/EditPartnerSettings';
import { getPartners, deletePartner } from 'core/API/partners';
import { CircularProgress, Box, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Button as MuiButton } from '@mui/material';

export interface PartnerSettingsProps {
	handleFocusChange?: (focus: 'organization' | 'workplace' | 'user' | 'partners' | null) => void;
}

function PartnerSettings(props: PartnerSettingsProps) {
	const { handleFocusChange } = props;

	// State management for partners
	const [partners, setPartners] = useState<Partner[]>([]);
	const [partnersLoading, setPartnersLoading] = useState<boolean>(false);
	const [partnersError, setPartnersError] = useState<string | null>(null);

	const [viewMode, setViewMode] = useState<'list' | 'create' | 'show' | 'edit'>('list');
	const [selectedPartner, setSelectedPartner] = useState<Partner | null>(null);
	const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
	const [partnerToDelete, setPartnerToDelete] = useState<Partner | null>(null);

	// Infinite scrolling state
	const [displayedPartnersCount, setDisplayedPartnersCount] = useState<number>(20);
	const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
	const tableContainerRef = useRef<HTMLDivElement>(null);
	const lastScrollTimeRef = useRef<number>(0);

	// Calculate partners to display
	const totalPartners = partners?.length || 0;
	const currentPartners = useMemo(() => {
		if (!partners) return [];

		// Filter out deleted partners
		const activePartners = partners.filter(p => !p.deleted);

		// Sort partners alphabetically by label
		const sortedPartners = [...activePartners].sort((a, b) =>
			(a.label || '').localeCompare(b.label || '', undefined, { sensitivity: 'base' })
		);

		// Return only the number of partners we want to display for infinite scrolling
		return sortedPartners.slice(0, displayedPartnersCount);
	}, [partners, displayedPartnersCount]);
	// Load partners function (extracted so it can be reused for refetching)
	const loadPartners = useCallback(async () => {
		try {
			setPartnersLoading(true);
			setPartnersError(null);
			const partnersList = await getPartners({
				limit: 30,
				page: 1,
			});
			setPartners(partnersList);
			setDisplayedPartnersCount(20);
		} catch (err: any) {
			console.error('Failed to fetch partners from API:', err);
			setPartnersError(err.message || 'Failed to fetch partners');
			setPartners([]);
		} finally {
			setPartnersLoading(false);
		}
	}, []);
	// Fetch users (keeping original useEffect as it’s not part of divisions)
	useEffect(() => {
		const loadPartners = async () => {
			try {
				setPartnersLoading(true);
				setPartnersError(null);
				const partnersList = await getPartners({
					limit: 30,
					page: 1,
				});
				setPartners(partnersList);
				setDisplayedPartnersCount(20);
			} catch (err: any) {
				console.error('Failed to fetch partners from API:', err);
				setPartnersError(err.message || 'Failed to fetch partners');
				setPartners([]);
			} finally {
				setPartnersLoading(false);
			}
		};

		loadPartners();
	}, []);

	// Infinite scroll handler with throttling
	const handleScroll = useCallback(() => {
		const now = Date.now();
		if (now - lastScrollTimeRef.current < 150) return; // Throttle to 150ms
		lastScrollTimeRef.current = now;

		const container = tableContainerRef.current;
		if (!container || isLoadingMore) return;

		const { scrollTop, scrollHeight, clientHeight } = container;
		const scrolledPercentage = (scrollTop + clientHeight) / scrollHeight;

		// Load more when scrolled 85% of the container
		if (scrolledPercentage > 0.85 && displayedPartnersCount < totalPartners) {
			setIsLoadingMore(true);
			// Use requestAnimationFrame for smoother performance
			requestAnimationFrame(() => {
				setDisplayedPartnersCount((prev) => Math.min(prev + 15, totalPartners));
				setIsLoadingMore(false);
			});
		}
	}, [displayedPartnersCount, totalPartners, isLoadingMore]);

	// Add scroll event listener
	useEffect(() => {
		const container = tableContainerRef.current;
		if (container) {
			container.addEventListener('scroll', handleScroll);
			return () => container.removeEventListener('scroll', handleScroll);
		}
	}, [handleScroll]);

	const handleCreateClick = () => {
		handleFocusChange?.('partners');
		setViewMode('create');
	};

	const handleBackClick = () => {
		handleFocusChange?.(null);
		setViewMode('list');
		setSelectedPartner(null);
	};

	const handleShowClick = (partner: Partner) => {
		console.log(`Show Partner ${partner.id}`);
		handleFocusChange?.('partners');
		setSelectedPartner(partner);
		setViewMode('show');
	};

	const handleEditClick = (partner: Partner) => {
		console.log(`Edit Partner ${partner.id}`);
		handleFocusChange?.('partners');
		setSelectedPartner(partner);
		setViewMode('edit');
	};

	const handleDeleteClick = (partner: Partner) => {
		console.log(`Delete Partner ${partner.id}`);
		setPartnerToDelete(partner);
		setDeleteDialogOpen(true);
	};

	const handleConfirmDelete = async () => {
		if (partnerToDelete) {
			try {
				await deletePartner(partnerToDelete.id);
				console.log(`Partner ${partnerToDelete.label} deleted successfully`);
				// Refresh the partners list
				await loadPartners();
			} catch (error) {
				console.error('Error deleting partner:', error);
			}
		}
		setDeleteDialogOpen(false);
		setPartnerToDelete(null);
	};

	const handleCancelDelete = () => {
		setDeleteDialogOpen(false);
		setPartnerToDelete(null);
	};

	switch (viewMode) {
		case 'list':
			return (
				<>
					{/* Partners Card */}
					<div className={styles.workplaceCard}>
						<div className={styles.header}>
							<h2 className={styles.formTitle}>Partners</h2>
							<Button className={styles.addButton} onClick={handleCreateClick}>
								Add
							</Button>
						</div>

					{partnersError && <div className={styles.errorMessage}>{partnersError}</div>}

					{partnersLoading ? (
						<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
					<CircularProgress size={20} color="inherit" />
						</Box>
					) : partners && partners.length > 0 ? (
							<div
								className={styles.tableContainer}
								ref={tableContainerRef}
								style={{
									maxHeight: '500px',
									overflow: 'auto'
								}}
							>
								<table className={`${styles.workplaceTable} ${styles.userTable}`} style={{ tableLayout: 'auto' }}>
									<thead>
										<tr>
											<th style={{ width: '60%', minWidth: '200px' }}>Partner Name</th>
											<th style={{ width: '20%', minWidth: '100px', textAlign: 'center' }}>Active</th>
											<th style={{ width: '48px', padding: '8px', textAlign: 'center' }}>View</th>
											<th style={{ width: '48px', padding: '8px', textAlign: 'center' }}>Edit</th>
											<th style={{ width: '48px', padding: '8px', textAlign: 'center' }}>Delete</th>
										</tr>
									</thead>
									<tbody>
										{currentPartners.map((partner: Partner) => (
											<tr key={partner?.id}>
												<td style={{ whiteSpace: 'nowrap' }}>{partner?.label}</td>
												<td style={{ textAlign: 'center' }}>{partner?.active ? 'Yes' : 'No'}</td>
												<td style={{ padding: '8px', width: '48px' }}>
													<div style={{ display: 'flex', justifyContent: 'center' }}>
														<button
															className={styles.iconButton}
															onClick={() => handleShowClick(partner)}
															title="View Partner"
														>
															<Visibility className={styles.editIcon} />
														</button>
													</div>
												</td>
												<td style={{ padding: '8px', width: '48px' }}>
													<div style={{ display: 'flex', justifyContent: 'center' }}>
														<button
															className={styles.iconButton}
															onClick={() => handleEditClick(partner)}
															title="Edit Partner"
														>
															<Edit className={styles.editIcon} />
														</button>
													</div>
												</td>
												<td style={{ padding: '8px', width: '48px' }}>
													<div style={{ display: 'flex', justifyContent: 'center' }}>
														<button
														className={styles.iconButton}
														onClick={() => handleDeleteClick(partner)}
														title="Delete Partner"
														>
															<Delete className={styles.deleteIcon} />
														</button>
													</div>
												</td>
											</tr>
										))}
									</tbody>
								</table>

								{/* Loading indicator for infinite scroll */}
								{isLoadingMore && (
									<div
										className={styles.loadingMore}
										style={{ padding: '10px', textAlign: 'center' }}
									>
										Loading more partners...
									</div>
								)}
							</div>
						) : !partnersLoading && !partnersError ? (
							<div className={styles.noDataMessage}>No partners found.</div>
						) : null}
					</div>

					{/* Delete Confirmation Dialog */}
					<Dialog
						open={deleteDialogOpen}
						onClose={handleCancelDelete}
						aria-labelledby="delete-dialog-title"
						aria-describedby="delete-dialog-description"
					>
						<DialogTitle id="delete-dialog-title">Confirm Delete</DialogTitle>
						<DialogContent>
							<DialogContentText id="delete-dialog-description">
								Are you sure you want to delete the partner &quot;{partnerToDelete?.label}&quot;? 
								This action cannot be undone.
							</DialogContentText>
						</DialogContent>
						<DialogActions>
							<Button onClick={handleCancelDelete} color="primary" className={styles.mabelButton}>
								Cancel
							</Button>
							<Button onClick={handleConfirmDelete} color="error" className={styles.mabelButton} autoFocus>
								Delete
							</Button>
						</DialogActions>
					</Dialog>
				</>
			);
		case 'create':
			return (
				<div className={styles.createWorkplaceContainer}>
					<CreateUserSettings
						backButtonClick={handleBackClick}
						onUserCreated={() => {
							loadPartners();
						}}
					/>
				</div>
			);
		case 'edit':
			return (
				<div className={styles.showWorkplaceContainer}>
					{selectedPartner && (
						<EditUserSettings 
							user={selectedPartner as any} 
							handleBackClick={() => {
								loadPartners();
								handleBackClick();
							}} 
						/>
					)}
				</div>
			);
		default:
			return (
				<div className={styles.showWorkplaceContainer}>
					<div className={styles.header}>
						<h2 className={styles.formTitle}>Partner Details</h2>
						<Button className={styles.backButton} onClick={handleBackClick}>
							Back
						</Button>
					</div>
					{selectedPartner && <ShowPartnerSetting partner={selectedPartner} />}
				</div>
			);
	}
}

export default PartnerSettings;
